import React, { useState, useRef } from "react";
import ParticipantAutocomplete from "./ParticipantAutocomplete";
import styles from "./buttonAnim.module.css";
// Importar icono SVG desde icons/
import { UserIcon } from "./icons/UserIcon";
import { TrashIcon } from "./icons/TrashIcon";
import { CleanIcon } from "./icons/CleanIcon";
import { CameraIcon } from "./icons/CameraIcon";
import { EnvelopeIcon } from "./icons/EnvelopeIcon";
import InvitationPreviewModal from "./InvitationPreviewModal";
import { generarInvitacion } from "../../utils/invitation/generarInvitacion";
import buildImageUrl from "../../config/imageUrlHelper";
import { API_BASE_URL } from "../../config/apiConfig";

// Fuente para la vista previa. Los avatares remotos (CDN de Instagram, X,
// unavatar, etc.) fallan al pintarse directamente en <img> por hotlink/CORS o
// bloqueadores; los servimos por el proxy mismo-origen (que ya los descarga
// correctamente). Los data:/blob: y rutas locales van tal cual.
const buildPreviewSrc = (imagen) => {
  const value = String(imagen || "").trim();
  if (!value) return "";
  if (value.startsWith("data:") || value.startsWith("blob:")) return value;
  if (/^https?:\/\//i.test(value)) {
    return `${API_BASE_URL}/proxy_avatar.php?url=${encodeURIComponent(value)}`;
  }
  return buildImageUrl(value);
};

export default function RankingParticipantManager() {
    // Ref para acceder a métodos del autocomplete
    const [autocompleteKey, setAutocompleteKey] = useState(0);
    const [clearAutocomplete, setClearAutocomplete] = useState(false);
    const [lastDeletedId, setLastDeletedId] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [form, setForm] = useState({
    imagen: "",
    nombre: "",
    preguntas_acertadas: 0,
    comodines_restantes: 0,
    rrss: "",
  });
  const [isExisting, setIsExisting] = useState(false);
  const [error, setError] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletedName, setDeletedName] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isResolvingAvatar, setIsResolvingAvatar] = useState(false);
  const [avatarHint, setAvatarHint] = useState("");
  const [imageSource, setImageSource] = useState("none");

  // Invitación
  const socialInfoRef = useRef(null); // cache: { rrss, avatar_url, identifier, platform }
  const [generandoInvitacion, setGenerandoInvitacion] = useState(false);
  const [invitacionUrl, setInvitacionUrl] = useState("");
  const [showInvitacion, setShowInvitacion] = useState(false);

  const handleDelete = async () => {
    if (!participant?.id) return;
    setError("");
    try {
      const { API_BASE_URL } = await import("../../config/apiConfig");
      const res = await fetch(`${API_BASE_URL}/eliminar_participante_ranking.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: participant.id }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || "Error al eliminar participante");
        return;
      }
      setDeletedName(participant.nombre);
      setLastDeletedId(participant.id);
      setForm({ imagen: "", nombre: "", preguntas_acertadas: 0, comodines_restantes: 0, rrss: "" });
      setImageSource("none");
      setAvatarHint("");
      setParticipant(null);
      setIsExisting(false);
      setError("");
      setShowDeleteModal(false);
      // Forzar recarga del autocomplete
      setAutocompleteKey(k => k + 1);
    } catch (err) {
      setError("Error de red al eliminar participante");
    }
  };

  const handleSelect = (p) => {
    if (p) {
      setParticipant(p);
      setForm({ ...p });
      setImageSource(p.imagen ? "manual" : "none");
      setAvatarHint("");
      setIsExisting(true);
    } else {
      setParticipant(null);
      setForm({ imagen: "", nombre: "", preguntas_acertadas: 0, comodines_restantes: 0, rrss: "" });
      setImageSource("none");
      setAvatarHint("");
      setIsExisting(false);
    }
    setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "rrss") {
      setAvatarHint("");
    }
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarHint("Subiendo imagen...");
    try {
      const { API_BASE_URL } = await import("../../config/apiConfig");
      const formData = new FormData();
      formData.append("imagen", file);
      const res = await fetch(`${API_BASE_URL}/subir_foto_participante.php`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (!data.success) {
        setAvatarHint(data.message || "No se pudo subir la imagen.");
        return;
      }
      setForm((f) => ({ ...f, imagen: data.imagen }));
      setImageSource("manual");
      setAvatarHint("Imagen manual seleccionada.");
    } catch (err) {
      setAvatarHint("Error de red al subir la imagen.");
    }
  };

  const handleResolveAvatarFromRrss = async (forceReplace = false) => {
    const rrss = form.rrss.trim();
    if (!rrss) {
      setAvatarHint("Introduce un enlace social para detectar la foto.");
      return;
    }

    setIsResolvingAvatar(true);
    setError("");
    setAvatarHint("");

    try {
      const { API_BASE_URL } = await import("../../config/apiConfig");
      const res = await fetch(`${API_BASE_URL}/obtener_avatar_rrss.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rrss }),
      });

      const data = await res.json();
      if (!data.success || !data.avatar_url) {
        setAvatarHint(data.message || "No se pudo detectar una imagen automática para este enlace.");
        return;
      }

      // Cachear datos sociales resueltos para reutilizarlos en la invitación.
      socialInfoRef.current = {
        rrss,
        avatar_url: data.avatar_url,
        identifier: data.identifier || "",
        platform: data.platform || "",
      };

      if (forceReplace || !form.imagen || imageSource === "social") {
        setForm((f) => ({ ...f, imagen: data.avatar_url }));
        setImageSource("social");
        setAvatarHint(`Imagen detectada automáticamente (${data.platform}).`);
      } else {
        setAvatarHint("Se detectó imagen automática, pero se conserva la imagen manual actual.");
      }
    } catch (err) {
      setAvatarHint("No se pudo consultar la imagen automática en este momento.");
    } finally {
      setIsResolvingAvatar(false);
    }
  };

  // Resuelve foto + usuario reutilizando la lógica existente (caché o endpoint).
  const resolverSocial = async (rrss) => {
    const cache = socialInfoRef.current;
    if (cache && cache.rrss === rrss && (cache.avatar_url || cache.identifier)) {
      return cache;
    }
    const { API_BASE_URL } = await import("../../config/apiConfig");
    const res = await fetch(`${API_BASE_URL}/obtener_avatar_rrss.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rrss }),
    });
    const data = await res.json();
    if (data && data.success) {
      const info = {
        rrss,
        avatar_url: data.avatar_url || "",
        identifier: data.identifier || "",
        platform: data.platform || "",
      };
      socialInfoRef.current = info;
      return info;
    }
    return { rrss, avatar_url: "", identifier: "", platform: "", message: data && data.message };
  };

  const handleGenerarInvitacion = async () => {
    setError("");
    setSuccessMessage("");

    // Validaciones: nombre del participante y red social.
    const faltan = [];
    if (!form.nombre.trim()) faltan.push("el nombre del participante");
    if (!form.rrss.trim()) faltan.push("la red social");
    if (faltan.length > 0) {
      const lista =
        faltan.length === 1 ? faltan[0] : faltan.slice(0, -1).join(", ") + " y " + faltan[faltan.length - 1];
      setError(`No se puede generar la invitación: falta ${lista}.`);
      return;
    }

    setGenerandoInvitacion(true);
    try {
      // Reutilizar (o resolver) foto de perfil y nombre de usuario.
      const social = await resolverSocial(form.rrss.trim());
      const imagen = form.imagen || social.avatar_url;

      if (!imagen) {
        setError(
          "No se pudo obtener la foto de perfil a partir de la red social indicada. Detecta o sube una imagen antes de generar la invitación."
        );
        return;
      }

      const dataUrl = await generarInvitacion({
        nombre: form.nombre.trim(),
        imagen: buildImageUrl(imagen),
        usuario: social.identifier,
        plataforma: social.platform,
      });

      setInvitacionUrl(dataUrl);
      setShowInvitacion(true);
    } catch (err) {
      setError(
        (err && err.message) ? `No se pudo generar la invitación: ${err.message}` : "No se pudo generar la invitación."
      );
    } finally {
      setGenerandoInvitacion(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // Validación frontend
    if (!form.nombre.trim()) {
      setError("El nombre es obligatorio");
      setSuccessMessage("");
      return;
    }
    // Validar que no haya duplicados si es nuevo
    if (!isExisting && participant && participant.nombre && participant.nombre.toLowerCase() === form.nombre.trim().toLowerCase()) {
      setError("Ya existe un participante con ese nombre");
      setSuccessMessage("");
      return;
    }
    try {
      const { API_BASE_URL } = await import("../../config/apiConfig");
      const res = await fetch(`${API_BASE_URL}/guardar_participante_ranking.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          autofetch_imagen: !!form.rrss.trim() && (!form.imagen || imageSource === "social"),
          id: isExisting ? participant.id : undefined,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || "Error al guardar participante");
        setSuccessMessage("");
        return;
      }
      setError("");
      if (!isExisting) {
        setSuccessMessage(`${form.nombre} creado`);
        setForm({ imagen: "", nombre: "", preguntas_acertadas: 0, comodines_restantes: 0, rrss: "" });
        setImageSource("none");
        setAvatarHint("");
        setParticipant(null);
        setIsExisting(false);
        // Forzar recarga del autocomplete para que aparezca el nuevo
        setAutocompleteKey(k => k + 1);
      } else {
        setSuccessMessage(`${form.nombre} actualizado`);
        setParticipant({ ...participant, ...form, imagen: data.imagen || form.imagen });
        if (data.avatar_detectado) {
          setImageSource("social");
        }
        // Forzar recarga del autocomplete para reflejar cambios
        setAutocompleteKey(k => k + 1);
      }
    } catch (err) {
      setError("Error de red al guardar participante");
      setSuccessMessage("");
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto px-1 sm:px-0">
      <header className="flex flex-wrap items-center gap-3 mb-4">
        <UserIcon className="w-8 h-8 text-primary" />
        <div>
          <h3 className="font-bold text-lg text-white">Gestión de Participantes</h3>
          <p className="text-gray-400 text-sm">Busca, edita o crea participantes del ranking.</p>
        </div>
        {isExisting && <span className="ml-auto px-3 py-1 rounded-full bg-green-700 text-xs text-white">Participante existente</span>}
      </header>
      <ParticipantAutocomplete
        key={autocompleteKey}
        onSelect={handleSelect}
        onDeleteParticipant={lastDeletedId}
        clearQuery={clearAutocomplete}
        onClearQueryHandled={() => setClearAutocomplete(false)}
      />
      {successMessage && (
        <span className="block mb-4 text-green-400 bg-green-900/60 rounded-lg px-4 py-2 font-semibold">
          {successMessage}
        </span>
      )}
      {deletedName && (
        <span className="block mb-4 text-green-400 bg-green-900/60 rounded-lg px-4 py-2 font-semibold">
          El usuario <span className="font-bold">{deletedName}</span> ha sido borrado correctamente.
        </span>
      )}
      <form className="mt-6 space-y-4" onSubmit={handleSubmit} autoComplete="off">
        <div className="flex flex-row items-center gap-3">
          <label className="block">
            <span className="block text-gray-300 mb-1">Imagen</span>
            <input type="file" accept="image/*" className="hidden" id="img-upload" onChange={handleImage} />
            <label htmlFor="img-upload" className="cursor-pointer w-16 h-16 rounded-full bg-gray-800 border-2 border-primary flex items-center justify-center overflow-hidden">
              {form.imagen ? (
                <img src={buildPreviewSrc(form.imagen)} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <UserIcon className="w-8 h-8 text-gray-500" />
              )}
            </label>
          </label>
          <div className="flex-1 min-w-0">
            <label className="block text-gray-300 mb-1">Nombre *</label>
            <input name="nombre" value={form.nombre} onChange={handleChange} required className="w-full rounded-lg bg-gray-900 text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary transition" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <label className="block">
            <span className="block text-gray-300 mb-1">Preguntas acertadas</span>
            <input type="number" name="preguntas_acertadas" value={form.preguntas_acertadas} onChange={handleChange} min={0} className="w-full rounded-lg bg-gray-900 text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary transition" />
          </label>
          <label className="block">
            <span className="block text-gray-300 mb-1">Comodines restantes</span>
            <input type="number" name="comodines_restantes" value={form.comodines_restantes} onChange={handleChange} min={0} className="w-full rounded-lg bg-gray-900 text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary transition" />
          </label>
        </div>
        <label className="block">
          <div className="flex items-center justify-between mb-1 gap-3">
            <span className="block text-gray-300">RRSS</span>
            <button
              type="button"
              onClick={() => handleResolveAvatarFromRrss(true)}
              className="text-xs px-3 py-1 rounded-md border border-primary/60 text-primary hover:bg-primary/10 transition disabled:opacity-60 inline-flex items-center gap-1"
              disabled={isResolvingAvatar}
            >
              <CameraIcon className="w-3.5 h-3.5" />
              {isResolvingAvatar ? "Detectando..." : "Detectar foto"}
            </button>
          </div>
          <input
            name="rrss"
            value={form.rrss}
            onChange={handleChange}
            onBlur={() => {
              if (!form.rrss.trim()) return;
              if (!form.imagen || imageSource === "social") {
                handleResolveAvatarFromRrss(false);
              }
            }}
            className="w-full rounded-lg bg-gray-900 text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary transition"
            placeholder="https://"
          />
          <span className="mt-2 block text-xs text-gray-500">
            Detección automática: Twitch, YouTube, X/Twitter, Instagram, TikTok, Kick y Facebook (páginas). Si alguna no se detecta, sube la foto manualmente.
          </span>
          {avatarHint && <span className="mt-2 block text-xs text-gray-400">{avatarHint}</span>}
        </label>
        {error && <div className="text-red-500 text-sm">{error}</div>}

        <div className="flex flex-wrap gap-2 mt-4 items-center w-full">
          <div className={styles["button-anim-group"]} style={{ minWidth: 0, position: "relative" }}>
            <button type="submit" className="w-auto bg-primary text-white font-bold px-3 py-2 rounded-lg shadow hover:brightness-110 transition text-xs sm:text-sm whitespace-nowrap">
              <span className="sm:hidden">Crear</span>
              <span className="hidden sm:inline">{isExisting ? "Guardar cambios" : "Crear participante"}</span>
            </button>
          </div>

          <button
            type="button"
            onClick={handleGenerarInvitacion}
            disabled={generandoInvitacion}
            title="Generar invitación con los datos del formulario"
            className="inline-flex items-center justify-center gap-1.5 border border-primary/60 text-primary px-3 py-2 rounded-lg hover:bg-primary/10 transition font-bold text-xs sm:text-sm whitespace-nowrap disabled:opacity-60"
          >
            <EnvelopeIcon className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline">{generandoInvitacion ? "Generando..." : "Invitación"}</span>
          </button>

          <div className={styles["button-anim-group"]} style={{ minWidth: 0, position: "relative", marginLeft: "auto" }}>
            <button
              type="button"
              className={`border border-gray-500 text-gray-300 px-3 py-2 rounded-lg hover:bg-gray-800 transition flex items-center justify-center gap-1.5 text-xs sm:text-sm whitespace-nowrap ${styles["clean-anim"]} ${isExisting ? styles["shift"] + " " + styles["with-gap"] : styles["unshift"]}`}
              onClick={() => {
                handleSelect(null);
                setClearAutocomplete(true);
              }}
              title="Limpiar formulario"
              style={{ zIndex: 2, background: "inherit" }}
            >
              <span>Limpiar</span>
              <CleanIcon className="w-4 h-4 shrink-0" />
            </button>

            <button
              type="button"
              className={`bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-1.5 transition font-bold text-xs sm:text-sm whitespace-nowrap ${styles["delete-anim"]} ${isExisting ? styles["visible"] : ""}`}
              onClick={() => setShowDeleteModal(true)}
              title="Eliminar participante"
              tabIndex={isExisting ? 0 : -1}
              aria-hidden={!isExisting}
              style={{ pointerEvents: isExisting ? "auto" : "none" }}
            >
              {/* <span>Eliminar</span> */}
              <TrashIcon className="w-4 h-4 shrink-0" />
            </button>
          </div>
        </div>

      </form>
      {/* Modal de confirmación de borrado */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowDeleteModal(false)}
            style={{ zIndex: 0 }}
          />
          {/* Modal */}
          <div className="bg-gray-900 rounded-xl shadow-xl p-6 max-w-sm w-full max-h-[90vh] overflow-y-auto relative animate-fade-in z-10">
            <h2 className="text-xl font-bold text-red-500 mb-2">Eliminar participante</h2>
            <p className="text-white mb-4">Estás a punto de eliminar al participante <span className="font-bold text-red-400">{participant?.nombre}</span>.<br/>Esta acción es <span className="font-bold">irreversible</span> y eliminará todos sus datos del ranking.</p>
            <div className="flex gap-3 mt-4">
              <button
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex-1 font-bold"
                onClick={handleDelete}
              >
                Sí, eliminar
              </button>
              <button
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex-1 font-bold"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de previsualización de la invitación */}
      <InvitationPreviewModal
        open={showInvitacion}
        dataUrl={invitacionUrl}
        nombre={form.nombre}
        onClose={() => setShowInvitacion(false)}
      />
    </div>
  );
}
